import { persistentCache } from '../../services/persistentCache';
import { AgentSessionSummary } from '../../services/api';
import {
  safeLocalStorageGet,
  safeLocalStorageSet,
} from '../../utils/safeStorage';

export const AGENTIC_CACHE_SCHEMA_VERSION = 3;

const getCacheInstanceStorageKey = (userId: string) => `agentic_cache_instance:${userId}`;

const createCacheInstanceId = (): string => (
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
);

export const ensureAgenticCacheMeta = async (
  userId: string,
): Promise<'cold' | 'warm' | 'reset'> => {
  if (!userId) return 'cold';
  const writeEpoch = persistentCache.captureWriteEpoch();
  const isCurrentScope = () => persistentCache.isWriteAllowed(
    userId,
    writeEpoch,
  );

  const storageKey = getCacheInstanceStorageKey(userId);
  const localInstanceId = safeLocalStorageGet(storageKey);
  const persistedMeta = await persistentCache.getAgenticCacheMeta<{
    schemaVersion: number;
    cacheInstanceId: string;
  }>(userId);
  if (!isCurrentScope()) {
    return 'cold';
  }

  if (
    persistedMeta
    && persistedMeta.schemaVersion === AGENTIC_CACHE_SCHEMA_VERSION
    && persistedMeta.cacheInstanceId
  ) {
    if (localInstanceId !== persistedMeta.cacheInstanceId) {
      safeLocalStorageSet(storageKey, persistedMeta.cacheInstanceId);
      return localInstanceId ? 'reset' : 'warm';
    }
    return 'warm';
  }

  if (persistedMeta || localInstanceId) {
    await persistentCache.clearAgenticCache(userId);
    if (!isCurrentScope()) {
      return 'cold';
    }
  }

  const nextInstanceId = localInstanceId || createCacheInstanceId();
  if (!isCurrentScope()) {
    return 'cold';
  }
  safeLocalStorageSet(storageKey, nextInstanceId);
  await persistentCache.setAgenticCacheMeta(userId, {
    schemaVersion: AGENTIC_CACHE_SCHEMA_VERSION,
    cacheInstanceId: nextInstanceId,
  });
  return localInstanceId ? 'reset' : 'cold';
};

export const loadCachedAgenticSummaries = async (
  userId: string,
): Promise<AgentSessionSummary[]> => {
  if (!userId) return [];
  return persistentCache.getAgenticSessionSummaries<AgentSessionSummary>(userId);
};

export const persistAgenticSummaries = async (
  userId: string,
  summaries: AgentSessionSummary[],
  options: { replaceMissing?: boolean } = {},
): Promise<void> => {
  if (!userId) return;
  await persistentCache.setAgenticSessionSummaries(userId, summaries, options);
};

export const removeAgenticCacheSession = async (
  userId: string,
  sessionId: string,
): Promise<void> => {
  if (!userId || !sessionId) return;
  await persistentCache.removeAgenticSession(userId, sessionId);
};
